import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SeoHead from './SeoHead';
import SiteFooter from './SiteFooter';
import { openCookieSettings } from '../utils/cookieConsent';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50">
      <SeoHead
        title="Privacy & Cookies | Swapify"
        description="Lees hoe Swapify omgaat met privacy, gegevensverwerking en cookies, en beheer je cookievoorkeuren."
        canonicalPath="/privacy"
      />
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
              {/* Logo */}
                        <Link to="/" className="flex items-center space-x-2">
                          <div className="text-2xl font-bold bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
                            Swapify
                          </div>
                        </Link>
            <Link to="/" className="text-gray-700 hover:text-red-500 transition flex items-center space-x-2">
              <ArrowLeft className="w-5 h-5" />
              <span>Terug naar home</span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacybeleid</h1>
        <p className="text-gray-500 mb-8">Laatst bijgewerkt: februari 2026</p>

        <div className="bg-white rounded-2xl shadow-lg p-8 prose prose-lg max-w-none">

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Inleiding</h2>
          <p className="text-gray-700 mb-4">
            Swapify ("wij", "ons", "onze") beheert de website swapify.nl en bijbehorende diensten. 
            Wij respecteren je privacy en zetten ons in voor de bescherming van je persoonsgegevens 
            in overeenstemming met de Algemene Verordening Gegevensbescherming (EU) 2016/679 (AVG/GDPR) 
            en de Uitvoeringswet AVG (UAVG).
          </p>
          <p className="text-gray-700 mb-4">
            Dit privacybeleid legt uit welke persoonsgegevens wij verzamelen, waarom wij deze verzamelen, 
            hoe wij ze gebruiken en welke rechten je hebt.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Verwerkingsverantwoordelijke</h2>
          <p className="text-gray-700 mb-4">
            De verwerkingsverantwoordelijke voor je persoonsgegevens is:
          </p>
          <p className="text-gray-700 mb-1"><strong>STACKK B.V.</strong></p>
          <p className="text-gray-700 mb-1">KVK: 71562214</p>
          <p className="text-gray-700 mb-1">
            E-mail: <a href="mailto:hello@swapify.nl" className="text-green-600 hover:underline">hello@swapify.nl</a>
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Welke Persoonsgegevens Wij Verzamelen</h2>

          <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">3.1 Accountregistratie</h3>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li>E-mailadres</li>
            <li>Naam (zoals opgegeven bij registratie)</li>
            <li>Wachtwoord (versleuteld opgeslagen via Firebase Authentication)</li>
          </ul>

          <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">3.2 Cadeaubonnen Verkopen</h3>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li>IBAN (bankrekeningnummer) voor uitbetalingen</li>
            <li>Cadeaubongegevens (merk, waarde, kaartnummer, pincode)</li>
            <li>Geüploade foto's van cadeaubonnen</li>
          </ul>

          <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">3.3 Cadeaubonnen Kopen</h3>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li>E-mailadres (voor orderbevestiging en codelevering)</li>
            <li>Betaalgegevens (verwerkt door OPP; Swapify slaat geen betaalkaartgegevens op)</li>
            <li>Transactiegeschiedenis</li>
          </ul>

          <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">3.4 Automatisch Verzamelde Gegevens</h3>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li>IP-adres</li>
            <li>Browsertype en -versie</li>
            <li>Bezochte pagina's en bestede tijd</li>
            <li>Apparaatinformatie</li>
            <li>Cookies en vergelijkbare technologieën (zie sectie 9)</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Doel en Rechtsgrondslag</h2>
          <p className="text-gray-700 mb-4">Wij verwerken je persoonsgegevens voor de volgende doeleinden:</p>

          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm text-gray-700 border-collapse">
              <thead>
                <tr className="bg-green-50">
                  <th className="border border-gray-200 px-4 py-3 text-left font-bold">Doel</th>
                  <th className="border border-gray-200 px-4 py-3 text-left font-bold">Gebruikte Gegevens</th>
                  <th className="border border-gray-200 px-4 py-3 text-left font-bold">Rechtsgrondslag (AVG)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Accountbeheer", "Naam, e-mail, wachtwoord", "Art. 6(1)(b) – Overeenkomst"],
                  ["Verwerken van aankopen", "E-mail, betaalgegevens, transactiegeschiedenis", "Art. 6(1)(b) – Overeenkomst"],
                  ["Uitbetalingen aan verkopers", "IBAN, naam, e-mail", "Art. 6(1)(b) – Overeenkomst"],
                  ["Verificatie van cadeaubonnen", "Cadeaubongegevens, foto's", "Art. 6(1)(b) – Overeenkomst"],
                  ["Fraudepreventie", "Transactiepatronen, accountgegevens, IP", "Art. 6(1)(f) – Gerechtvaardigd belang"],
                  ["AML/CTF-naleving", "Identiteitsdocumenten, transactiegegevens", "Art. 6(1)(c) – Wettelijke verplichting"],
                  ["Klantenservice", "E-mail, naam, bestelgegevens", "Art. 6(1)(b) – Overeenkomst"],
                  ["Platformverbetering", "Analytics, apparaatinfo", "Art. 6(1)(f) – Gerechtvaardigd belang"],
                  ["E-mailnotificaties", "E-mailadres", "Art. 6(1)(a) – Toestemming"],
                ].map(([doel, gegevens, grondslag], i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-200 px-4 py-2">{doel}</td>
                    <td className="border border-gray-200 px-4 py-2">{gegevens}</td>
                    <td className="border border-gray-200 px-4 py-2">{grondslag}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Delen met Derden</h2>
          <p className="text-gray-700 mb-4">Wij delen persoonsgegevens alleen wanneer dit noodzakelijk is voor onze dienstverlening:</p>

          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm text-gray-700 border-collapse">
              <thead>
                <tr className="bg-green-50">
                  <th className="border border-gray-200 px-4 py-3 text-left font-bold">Derde Partij</th>
                  <th className="border border-gray-200 px-4 py-3 text-left font-bold">Doel</th>
                  <th className="border border-gray-200 px-4 py-3 text-left font-bold">Gedeelde Gegevens</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["OPP (Online Payment Platform)", "Betalingsverwerking & escrow", "Naam, e-mail, IBAN, transactiebedragen"],
                  ["Firebase (Google Cloud)", "Authenticatie, database, opslag", "E-mail, accountgegevens, cadeaubonfoto's"],
                  ["Postmark", "Transactionele e-mails", "E-mailadres, bestelgegevens"],
                  ["Netlify", "Websitehosting", "IP-adres, browsergegevens (serverlogs)"],
                  ["FIU-Nederland / Autoriteiten", "Wettelijke verplichtingen (AML/CTF)", "Zoals vereist door de wet"],
                ].map(([partij, doel, gegevens], i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-200 px-4 py-2">{partij}</td>
                    <td className="border border-gray-200 px-4 py-2">{doel}</td>
                    <td className="border border-gray-200 px-4 py-2">{gegevens}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-gray-700 mb-4">
            Wij verkopen, verhuren of verhandelen je persoonsgegevens niet. Wij delen geen gegevens met adverteerders.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Internationale Gegevensoverdracht</h2>
          <p className="text-gray-700 mb-4">
            Sommige van onze dienstverleners (Firebase/Google Cloud, Postmark) kunnen gegevens verwerken buiten 
            de Europese Economische Ruimte (EER). Waar dit het geval is, zijn passende waarborgen getroffen:
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li>Google Cloud: EU-US Data Privacy Framework (DPF); Standard Contractual Clauses (SCCs)</li>
            <li>Postmark: Standard Contractual Clauses (SCCs) en DPF-certificering</li>
            <li>OPP: Gegevens worden binnen de EER verwerkt</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Bewaartermijnen</h2>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li><strong>Accountgegevens:</strong> Bewaard gedurende de looptijd van je account, plus 2 jaar na verwijdering</li>
            <li><strong>Transactiegegevens:</strong> 7 jaar (Nederlandse belastingwetgeving) en 5 jaar (Wwft)</li>
            <li><strong>Cadeauboncodes:</strong> 6 maanden na de transactie, daarna permanent verwijderd</li>
            <li><strong>Serverlogs:</strong> 90 dagen</li>
            <li><strong>Fraudeonderzoeksgegevens:</strong> 5 jaar na sluiting van het dossier</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Jouw Rechten (AVG Artikelen 15-22)</h2>
          <p className="text-gray-700 mb-4">Op grond van de AVG heb je de volgende rechten:</p>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li><strong>Recht op inzage (Art. 15):</strong> Een kopie van je persoonsgegevens opvragen</li>
            <li><strong>Recht op rectificatie (Art. 16):</strong> Correctie van onjuiste gegevens verzoeken</li>
            <li><strong>Recht op verwijdering (Art. 17):</strong> Verwijdering van je gegevens verzoeken, onder voorbehoud van wettelijke bewaartermijnen</li>
            <li><strong>Recht op beperking (Art. 18):</strong> Beperkte verwerking van je gegevens verzoeken</li>
            <li><strong>Recht op overdraagbaarheid (Art. 20):</strong> Je gegevens ontvangen in een gestructureerd, machineleesbaar formaat</li>
            <li><strong>Recht van bezwaar (Art. 21):</strong> Bezwaar maken tegen verwerking op basis van gerechtvaardigd belang</li>
            <li><strong>Recht op intrekking van toestemming (Art. 7):</strong> Toestemming op elk moment intrekken</li>
          </ul>
          <p className="text-gray-700 mb-4">
            Om je rechten uit te oefenen, mail ons op{' '}
            <a href="mailto:hello@swapify.nl" className="text-green-600 hover:underline font-semibold">hello@swapify.nl</a>. 
            Wij reageren binnen 30 dagen.
          </p>
          <p className="text-gray-700 mb-4">
            Indien je niet tevreden bent met onze reactie, kun je een klacht indienen bij de 
            Autoriteit Persoonsgegevens (AP):{' '}
            <a href="https://www.autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline font-semibold">
              www.autoriteitpersoonsgegevens.nl
            </a>.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Cookies</h2>
          <p className="text-gray-700 mb-4">Swapify maakt gebruik van de volgende cookies en vergelijkbare technologieën:</p>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li><strong>Strikt noodzakelijke cookies:</strong> Authenticatietokens, sessiebeheer, winkelwagenfunctionaliteit. Deze zijn essentieel voor het functioneren van het platform en vereisen geen toestemming.</li>
            <li><strong>Analytische cookies:</strong> Staan momenteel standaard uit. Als Swapify deze later inzet, worden ze alleen gebruikt na jouw toestemming.</li>
          </ul>
          <p className="text-gray-700 mb-4">
            Swapify maakt geen gebruik van advertentiecookies of trackingpixels. Je kunt cookievoorkeuren beheren via de cookie-instellingen op de site of via je browserinstellingen.
          </p>
          <button
            type="button"
            onClick={openCookieSettings}
            className="mb-4 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:shadow-lg"
          >
            Cookie-instellingen openen
          </button>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Beveiliging</h2>
          <p className="text-gray-700 mb-4">
            Wij treffen passende technische en organisatorische maatregelen om je persoonsgegevens te beschermen, waaronder:
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            <li>Versleuteling van gegevens in transit (TLS/HTTPS) en in rust</li>
            <li>Firebase Authentication met versleutelde wachtwoordopslag</li>
            <li>Cadeauboncodes opgeslagen in beveiligde Firestore-subcollecties met server-side toegangsregels</li>
            <li>Toegang tot persoonsgegevens beperkt tot bevoegd personeel</li>
            <li>Regelmatige evaluatie van beveiligingspraktijken</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Kinderen</h2>
          <p className="text-gray-700 mb-4">
            Swapify is niet bedoeld voor gebruik door kinderen onder de 16 jaar. Wij verzamelen niet bewust 
            persoonsgegevens van kinderen. Als wij constateren dat wij gegevens van een kind onder de 16 jaar 
            hebben verzameld, zullen wij deze onmiddellijk verwijderen.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Wijzigingen</h2>
          <p className="text-gray-700 mb-4">
            Wij kunnen dit privacybeleid van tijd tot tijd bijwerken. Wezenlijke wijzigingen worden gecommuniceerd 
            via e-mail of een prominente melding op onze website. De huidige versie is altijd beschikbaar op swapify.nl/privacy.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">13. Contact</h2>
          <p className="text-gray-700 mb-4">
            Voor vragen over dit privacybeleid of je persoonsgegevens, neem contact op via:{' '}
            <a href="mailto:hello@swapify.nl" className="text-green-600 hover:underline font-semibold">hello@swapify.nl</a>
          </p>

        </div>
      </div>
      <SiteFooter />
    </div>
  );
};

export default PrivacyPage;
